import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { IoArrowBack, IoCard, IoLockClosed, IoCheckmarkCircle } from 'react-icons/io5';
import useCartStore from '@/stores/CartStore';
import { useCreateOrder } from '@/hooks/useOrders';
import Input from '@/components/forms/Input/Input';
import Select from '@/components/forms/Select/Select';
import Button from '@/components/common/Button/Button';
import Spinner from '@/components/common/Spinner/Spinner';
import './Checkout.css';

interface AddressForm {
    full_name: string;
    company: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state_province: string;
    postal_code: string;
    country: string;
    phone: string;
}

interface CheckoutStep {
    id: 'shipping' | 'payment' | 'review';
    title: string;
    completed: boolean;
}

const Checkout: React.FC = () => {
    const navigate = useNavigate();
    const { items, subtotal, tax_amount, shipping_cost, total_amount, clearCart } = useCartStore();
    const { mutate: createOrder, isPending: isCreatingOrder } = useCreateOrder();

    const [currentStep, setCurrentStep] = useState<'shipping' | 'payment' | 'review'>('shipping');
    const [useBillingAsShipping, setUseBillingAsShipping] = useState(true);
    
    const [shippingAddress, setShippingAddress] = useState<AddressForm>({
        full_name: '',
        company: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'US',
        phone: '',
    });

    const [billingAddress, setBillingAddress] = useState<AddressForm>({
        full_name: '',
        company: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'US',
        phone: '',
    });

    const [paymentMethod, setPaymentMethod] = useState('credit_card');
    const [orderNotes, setOrderNotes] = useState('');
    
    // Payment form state
    const [cardNumber, setCardNumber] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [cvv, setCvv] = useState('');
    const [cardholderName, setCardholderName] = useState('');

    const steps: CheckoutStep[] = [
        { id: 'shipping', title: 'Información de Envío', completed: currentStep !== 'shipping' },
        { id: 'payment', title: 'Método de Pago', completed: currentStep === 'review' },
        { id: 'review', title: 'Revisar Pedido', completed: false },
    ];

    const countries = [
        { value: 'US', label: 'Estados Unidos' },
        { value: 'CA', label: 'Canadá' },
        { value: 'MX', label: 'México' },
        { value: 'GB', label: 'Reino Unido' },
        { value: 'DE', label: 'Alemania' },
        { value: 'FR', label: 'Francia' },
    ];

    const paymentMethods = [
        { value: 'credit_card', label: 'Tarjeta de Crédito' },
        { value: 'paypal', label: 'PayPal' },
        { value: 'bank_transfer', label: 'Transferencia Bancaria' },
    ];

    const handleAddressChange = (
        addressType: 'shipping' | 'billing',
        field: keyof AddressForm,
        value: string
    ) => {
        if (addressType === 'shipping') {
            setShippingAddress(prev => ({ ...prev, [field]: value }));
        } else {
            setBillingAddress(prev => ({ ...prev, [field]: value }));
        }
    };

    const validateAddress = (address: AddressForm): boolean => {
        return !!(
            address.full_name &&
            address.address_line_1 &&
            address.city &&
            address.state_province &&
            address.postal_code &&
            address.country
        );
    };

    const handleNextStep = () => {
        if (currentStep === 'shipping') {
            if (!validateAddress(shippingAddress)) {
                toast.error('Por favor complete todos los campos requeridos de la dirección de envío.');
                return;
            }
            setCurrentStep('payment');
        } else if (currentStep === 'payment') {
            setCurrentStep('review');
        }
    };

    const handlePreviousStep = () => {
        if (currentStep === 'payment') {
            setCurrentStep('shipping');
        } else if (currentStep === 'review') {
            setCurrentStep('payment');
        }
    };

    const handlePlaceOrder = () => {
        // Create payment method object
        const paymentMethodData = paymentMethod === 'credit_card' && cardNumber
            ? {
                type: paymentMethod,
                last_four: cardNumber.replace(/\s/g, '').slice(-4)
            }
            : {
                type: paymentMethod,
                last_four: undefined
            };

        const orderData = {
            items: items.map(item => ({
                product_id: item.product_id,
                quantity: item.quantity,
                material_id: item.customization.material_id,
                color_id: item.customization.color_id,
                size_id: item.customization.size_id,
                custom_text: item.customization.custom_text,
                custom_logo: item.customization.custom_logo,
                custom_pattern: item.customization.custom_pattern,
                notes: item.customization.notes,
            })),
            shipping_address: shippingAddress,
            billing_address: useBillingAsShipping ? shippingAddress : billingAddress,
            payment_method: paymentMethodData,
            notes: orderNotes,
        };

        createOrder(orderData, {
            onSuccess: (order) => {
                clearCart();
                navigate(`/order-success/${order.id}`);
            },
            onError: (error) => {
                console.error('Order creation failed:', error);
                toast.error('Error al realizar el pedido. Por favor contáctese con soporte.'); // TODO implement this
            },
        });
    };

    if (items.length === 0) {
        return (
            <div className="checkout_container">
                <div className="checkout_empty">
                    <h2>Su carrito está vacío</h2>
                    <p>Agregue algunos artículos a su carrito antes de proceder al pago.</p>
                    <Button onClick={() => navigate('/products')}>
                        Continuar Comprando
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="checkout_container">
            <div className="checkout_header">
                <Button
                    variant="secondary"
                    onClick={() => navigate('/cart')}
                >
                    <IoArrowBack size={16} />
                    Volver al Carrito
                </Button>
                <h1>Pago</h1>
            </div>

            {/* Progress Steps */}
            <div className="checkout_steps">
                {steps.map((step, index) => (
                    <div key={step.id} className={`checkout_step ${currentStep === step.id ? 'active' : ''} ${step.completed ? 'completed' : ''}`}>
                        <div className="step_indicator">
                            {step.completed ? (
                                <IoCheckmarkCircle size={20} />
                            ) : (
                                <span className="step_number">{index + 1}</span>
                            )}
                        </div>
                        <span className="step_title">{step.title}</span>
                        {index < steps.length - 1 && <div className="step_connector"></div>}
                    </div>
                ))}
            </div>

            <div className="checkout_content">
                <div className="checkout_main">
                    {/* Shipping Information */}
                    {currentStep === 'shipping' && (
                        <div className="checkout_section">
                            <h2>Información de Envío</h2>
                            <div className="address_form">
                                <div className="form_row">
                                    <Input
                                        name="full_name"
                                        label="Nombre Completo *"
                                        value={shippingAddress.full_name}
                                        setValue={(value) => handleAddressChange('shipping', 'full_name', value)}
                                        placeholder="Juan Pérez"
                                        required
                                    />
                                    <Input
                                        name="company"
                                        label="Empresa (Opcional)"
                                        value={shippingAddress.company}
                                        setValue={(value) => handleAddressChange('shipping', 'company', value)}
                                        placeholder="Nombre de la Empresa"
                                    />
                                </div>
                                
                                <Input
                                    name="address_line_1"
                                    label="Dirección Línea 1 *"
                                    value={shippingAddress.address_line_1}
                                    setValue={(value) => handleAddressChange('shipping', 'address_line_1', value)}
                                    placeholder="123 Calle Principal"
                                    required
                                />
                                
                                <Input
                                    name="address_line_2"
                                    label="Dirección Línea 2 (Opcional)"
                                    value={shippingAddress.address_line_2}
                                    setValue={(value) => handleAddressChange('shipping', 'address_line_2', value)}
                                    placeholder="Apartamento, suite, etc."
                                />
                                
                                <div className="form_row">
                                    <Input
                                        name="city"
                                        label="Ciudad *"
                                        value={shippingAddress.city}
                                        setValue={(value) => handleAddressChange('shipping', 'city', value)}
                                        placeholder="Ciudad de México"
                                        required
                                    />
                                    <Input
                                        name="state_province"
                                        label="Estado/Provincia *"
                                        value={shippingAddress.state_province}
                                        setValue={(value) => handleAddressChange('shipping', 'state_province', value)}
                                        placeholder="CDMX"
                                        required
                                    />
                                </div>
                                
                                <div className="form_row">
                                    <Input
                                        name="postal_code"
                                        label="Código Postal *"
                                        value={shippingAddress.postal_code}
                                        setValue={(value) => handleAddressChange('shipping', 'postal_code', value)}
                                        placeholder="01000"
                                        required
                                    />
                                    <Select
                                        label="País"
                                        value={shippingAddress.country}
                                        onChange={(e) => handleAddressChange('shipping', 'country', e.target.value)}
                                        options={countries}
                                        required
                                    />
                                </div>
                                
                                <Input
                                    name="phone"
                                    label="Número de Teléfono"
                                    value={shippingAddress.phone}
                                    setValue={(value) => handleAddressChange('shipping', 'phone', value)}
                                    placeholder="+52 (55) 123-4567"
                                />
                            </div>
                        </div>
                    )}

                    {/* Payment Method */}
                    {currentStep === 'payment' && (
                        <div className="checkout_section">
                            <h2>Payment Method</h2>
                            
                            <div className="payment_methods">
                                {paymentMethods.map(method => (
                                    <label key={method.value} className={`payment_method ${paymentMethod === method.value ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            value={method.value}
                                            checked={paymentMethod === method.value}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        />
                                        <div className="payment_method_content">
                                            <IoCard size={20} />
                                            <span>{method.label}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            
                            <Input
                                name="address_line_1"
                                label="Dirección Línea 1 *"
                                value={shippingAddress.address_line_1}
                                setValue={(value) => handleAddressChange('shipping', 'address_line_1', value)}
                                placeholder="123 Main Street"
                                required
                            />
                            
                            <Input
                                name="address_line_2"
                                label="Dirección Línea 2 (Opcional)"
                                value={shippingAddress.address_line_2}
                                setValue={(value) => handleAddressChange('shipping', 'address_line_2', value)}
                                placeholder="Apartment, suite, etc."
                            />
                            
                            <div className="form_row">
                                <Input
                                    name="city"
                                    label="Ciudad *"
                                    value={shippingAddress.city}
                                    setValue={(value) => handleAddressChange('shipping', 'city', value)}
                                    placeholder="Ciudad de México"
                                    required
                                />
                                <Input
                                    name="state_province"
                                    label="Estado/Provincia *"
                                    value={shippingAddress.state_province}
                                    setValue={(value) => handleAddressChange('shipping', 'state_province', value)}
                                    placeholder="Estado/Provincia"
                                    required
                                />
                            </div>
                            
                            <div className="form_row">
                                <Input
                                    name="postal_code"
                                    label="Código Postal *"
                                    value={shippingAddress.postal_code}
                                    setValue={(value) => handleAddressChange('shipping', 'postal_code', value)}
                                    placeholder="10001"
                                    required
                                />
                                <Select
                                    label="País"
                                    value={shippingAddress.country}
                                    onChange={(e) => handleAddressChange('shipping', 'country', e.target.value)}
                                    options={countries}
                                    required
                                />
                            </div>
                            
                            <Input
                                name="phone"
                                label="Número de Teléfono"
                                value={shippingAddress.phone}
                                setValue={(value) => handleAddressChange('shipping', 'phone', value)}
                                placeholder="+51 999 999 999"
                            />
                        </div>
                    )}

                    {/* Payment Method */}
                    {currentStep === 'payment' && (
                        <div className="checkout_section">
                            <h2>Método de Pago</h2>
                            
                            <div className="payment_methods">
                                {paymentMethods.map(method => (
                                    <label key={method.value} className={`payment_method ${paymentMethod === method.value ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            value={method.value}
                                            checked={paymentMethod === method.value}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        />
                                        <div className="payment_method_content">
                                            <IoCard size={20} />
                                            <span>{method.label}</span>
                                        </div>
                                    </label>
                                ))}
                        </div>

                        {paymentMethod === 'credit_card' && (
                            <div className="credit_card_form">
                                <Input
                                    name="card_number"
                                    label="Número de Tarjeta"
                                    value={cardNumber}
                                    setValue={setCardNumber}
                                    placeholder="1234 5678 9012 3456"
                                    startContent={<IoCard size={16} />}
                                />
                                <div className="form_row">
                                    <Input
                                        name="expiry_date"
                                        label="Fecha de Vencimiento"
                                        value={expiryDate}
                                        setValue={setExpiryDate}
                                        placeholder="MM/YY"
                                    />
                                    <Input
                                        name="cvv"
                                        label="CVV"
                                        value={cvv}
                                        setValue={setCvv}
                                        placeholder="123"
                                        endContent={<IoLockClosed size={16} />}
                                    />
                                </div>
                                <Input
                                    name="cardholder_name"
                                    label="Nombre del Titular"
                                    value={cardholderName}
                                    setValue={setCardholderName}
                                    placeholder="Juan Pérez"
                                />
                            </div>
                        )}

                            <div className="billing_address_section">
                                <label className="checkbox_label">
                                    <input
                                        type="checkbox"
                                        checked={useBillingAsShipping}
                                        onChange={(e) => setUseBillingAsShipping(e.target.checked)}
                                    />
                                    <span>La dirección de facturación es la misma que la dirección de envío</span>
                                </label>

                                {!useBillingAsShipping && (
                                    <div className="address_form">
                                        <h3>Dirección de Facturación</h3>
                                        <div className="form_row">
                                            <Input
                                                name="billing_full_name"
                                                label="Nombre Completo *"
                                                value={billingAddress.full_name}
                                                setValue={(value) => handleAddressChange('billing', 'full_name', value)}
                                                placeholder="Juan Pérez"
                                                required
                                            />
                                            <Input
                                                name="billing_company"
                                                label="Empresa (Opcional)"
                                                value={billingAddress.company}
                                                setValue={(value) => handleAddressChange('billing', 'company', value)}
                                                placeholder="Nombre de la Empresa"
                                            />
                                        </div>
                                        
                                        <Input
                                            name="billing_address_line_1"
                                            label="Dirección Línea 1 *"
                                            value={billingAddress.address_line_1}
                                            setValue={(value) => handleAddressChange('billing', 'address_line_1', value)}
                                            placeholder="123 Calle Principal"
                                            required
                                        />
                                        
                                        <div className="form_row">
                                            <Input
                                                name="billing_city"
                                                label="Ciudad *"
                                                value={billingAddress.city}
                                                setValue={(value) => handleAddressChange('billing', 'city', value)}
                                                placeholder="Ciudad de México"
                                                required
                                            />
                                            <Input
                                                name="billing_state_province"
                                                label="Estado/Provincia *"
                                                value={billingAddress.state_province}
                                                setValue={(value) => handleAddressChange('billing', 'state_province', value)}
                                                placeholder="CDMX"
                                                required
                                            />
                                        </div>
                                        
                                        <div className="form_row">
                                            <Input
                                                name="billing_postal_code"
                                                label="Postal Code *"
                                                value={billingAddress.postal_code}
                                                setValue={(value) => handleAddressChange('billing', 'postal_code', value)}
                                                placeholder="10001"
                                                required
                                            />
                                            <Select
                                                label="Country"
                                                value={billingAddress.country}
                                                onChange={(e) => handleAddressChange('billing', 'country', e.target.value)}
                                                options={countries}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Review Order */}
                    {currentStep === 'review' && (
                        <div className="checkout_section">
                            <h2>Revisar Su Pedido</h2>
                            
                            <div className="order_review">
                                <div className="review_section">
                                    <h3>Dirección de Envío</h3>
                                    <div className="address_display">
                                        <p>{shippingAddress.full_name}</p>
                                        {shippingAddress.company && <p>{shippingAddress.company}</p>}
                                        <p>{shippingAddress.address_line_1}</p>
                                        {shippingAddress.address_line_2 && <p>{shippingAddress.address_line_2}</p>}
                                        <p>{shippingAddress.city}, {shippingAddress.state_province} {shippingAddress.postal_code}</p>
                                        <p>{countries.find(c => c.value === shippingAddress.country)?.label}</p>
                                        {shippingAddress.phone && <p>{shippingAddress.phone}</p>}
                                    </div>
                                </div>

                                <div className="review_section">
                                    <h3>Método de Pago</h3>
                                    <p>{paymentMethods.find(p => p.value === paymentMethod)?.label}</p>
                                </div>

                                <div className="review_section">
                                    <h3>Artículos del Pedido</h3>
                                    <div className="review_items">
                                        {items.map(item => (
                                            <div key={item.id} className="review_item">
                                                <img
                                                    src={item.product_image || '/placeholder-product.jpg'}
                                                    alt={item.product_name}
                                                    className="review_item_image"
                                                />
                                                <div className="review_item_details">
                                                    <h4>{item.product_name}</h4>
                                                    <p>Cantidad: {item.quantity}</p>
                                                    {item.material_name && <p>Material: {item.material_name}</p>}
                                                    {item.color_name && <p>Color: {item.color_name}</p>}
                                                    {item.size_name && <p>Talla: {item.size_name}</p>}
                                                </div>
                                                <div className="review_item_price">
                                                    ${item.total_price.toFixed(2)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="review_section">
                                    <Input
                                        name="order_notes"
                                        label="Notas del Pedido (Opcional)"
                                        value={orderNotes}
                                        setValue={setOrderNotes}
                                        placeholder="Cualquier instrucción especial para su pedido..."
                                        multiline
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="checkout_navigation">
                        {currentStep !== 'shipping' && (
                            <Button
                                variant="secondary"
                                onClick={handlePreviousStep}
                            >
                                <IoArrowBack size={16} />
                                Anterior
                            </Button>
                        )}
                        
                        {currentStep !== 'review' ? (
                            <Button
                                variant="primary"
                                onClick={handleNextStep}
                                className="next_btn"
                            >
                                Continuar
                            </Button>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={handlePlaceOrder}
                                disabled={isCreatingOrder}
                                className="place_order_btn"
                            >
                                {isCreatingOrder ? <Spinner size="sm" /> : <IoLockClosed size={16} />}
                                {isCreatingOrder ? 'Realizando Pedido...' : 'Realizar Pedido'}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Order Summary Sidebar */}
                <div className="checkout_sidebar">
                    <div className="order_summary">
                        <h3>Resumen del Pedido</h3>
                        
                        <div className="summary_items">
                            {items.map(item => (
                                <div key={item.id} className="summary_item">
                                    <img
                                        src={item.product_image || '/placeholder-product.jpg'}
                                        alt={item.product_name}
                                        className="summary_item_image"
                                    />
                                    <div className="summary_item_details">
                                        <span className="summary_item_name">{item.product_name}</span>
                                        <span className="summary_item_quantity">Cant: {item.quantity}</span>
                                    </div>
                                    <span className="summary_item_price">${item.total_price.toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        <div className="summary_totals">
                            <div className="summary_line">
                                <span>Subtotal</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="summary_line">
                                <span>Impuestos</span>
                                <span>${tax_amount.toFixed(2)}</span>
                            </div>
                            <div className="summary_line">
                                <span>Envío</span>
                                <span>{shipping_cost === 0 ? 'Gratis' : `$${shipping_cost.toFixed(2)}`}</span>
                            </div>
                            <div className="summary_divider"></div>
                            <div className="summary_line summary_total">
                                <span>Total</span>
                                <span>${total_amount.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="security_badges">
                            <div className="security_badge">
                                <IoArrowBack size={16} />
                                <span>Pago Seguro</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
